import { Injectable, inject } from '@angular/core'
import { Subject } from 'rxjs'
import { AuthService } from '../auth.service'
import {
  CASHFLOW_ARGUMENT_FIELDS,
  CASHFLOW_DATE_FILTER_FIELDS,
  CASHFLOW_FILTER_FIELDS,
  CASHFLOW_SERIES_GROUP_FIELDS,
  CASHFLOW_VALUE_FIELDS,
} from 'src/app/types/datasources/cashflow.model'
import { ChartField } from 'src/app/types/chart.model'
import {
  CLAIM_ARGUMENT_FIELDS,
  CLAIM_DATE_FILTER_FIELDS,
  CLAIM_FILTER_FIELDS,
  CLAIM_SERIES_GROUP_FIELDS,
  CLAIM_VALUE_FIELDS,
} from 'src/app/types/datasources/claims.model'
import {
  DEFAULT_CHART_TYPE,
  DEFAULT_DATE_GROUPING,
  DEFAULT_VALUE_AGGREGATION,
  FUTURE_DATE_RANGE_TYPES,
  JOBNUM_FIELD,
  PAST_DATE_RANGE_TYPES,
} from 'src/app/types/constants'
import {
  ChartDataSource,
  ChartType,
  DateAxisType,
  DateRangeType,
  ValueAggregationType,
} from 'src/app/types/enums'
import {
  JOB_ARGUMENT_FIELDS,
  JOB_DATE_FILTER_FIELDS,
  JOB_FILTER_FIELDS,
  JOB_SERIES_GROUP_FIELDS,
  JOB_VALUE_FIELDS,
} from 'src/app/types/datasources/job.model'
import {
  JOBTASK_ARGUMENT_FIELDS,
  JOBTASK_DATE_FILTER_FIELDS,
  JOBTASK_FILTER_FIELDS,
  JOBTASK_SERIES_GROUP_FIELDS,
  JOBTASK_VALUE_FIELDS,
  TASK_DURATION_VALUE_FIELDS,
} from 'src/app/types/datasources/tasks.model'
import { LogService } from '../log.service'
import {
  FORECAST_TASKS_ARGUMENT_FIELDS,
  FORECAST_TASKS_DATE_FILTER_FIELDS,
  FORECAST_TASKS_FILTER_FIELDS,
  FORECAST_TASKS_SERIES_GROUP_FIELDS,
  FORECAST_TASKS_VALUE_FIELDS,
} from 'src/app/types/datasources/forecast-tasks.model'
import {
  FORECAST_TASKS_IN_PROGRESS_ARGUMENT_FIELDS,
  FORECAST_TASKS_IN_PROGRESS_DATE_FILTER_FIELDS,
  FORECAST_TASKS_IN_PROGRESS_FILTER_FIELDS,
  FORECAST_TASKS_IN_PROGRESS_SERIES_GROUP_FIELDS,
  FORECAST_TASKS_IN_PROGRESS_VALUE_FIELDS,
  FORECAST_TASK_IN_PROGRESS_GROUP_KEY_FUNC,
} from 'src/app/types/datasources/forecast-tasks-in-progress.model'
import {
  EXTRAS_ARGUMENT_FIELDS,
  EXTRAS_DATE_FILTER_FIELDS,
  EXTRAS_FILTER_FIELDS,
  EXTRAS_SERIES_GROUP_FIELDS,
  EXTRAS_VALUE_FIELDS,
} from 'src/app/types/datasources/extras.model'
import { buildRoleField, isRoleListed } from 'src/app/types/datasources/job-lookup.model'
import { DashboardJobLookupRoleDto } from 'src/app/types/dto/dashboard-job-lookup.dto'

@Injectable({
  providedIn: 'root',
})
export class DatasourceConfigService {
  private logger = inject(LogService);
  private auth = inject(AuthService);

  // Job roles are dynamic (from the job lookup - see JobLookupApiService). Keyed by company id so that
  // switching companies can never leave stale roles from a *different* company's last fetch in place
  private jobRolesByCompany = new Map<number, DashboardJobLookupRoleDto[]>()
  private jobRolesChangedSubject = new Subject<number>()
  readonly jobRolesChanged$ = this.jobRolesChangedSubject.asObservable()

  isDateArgument(datasource: ChartDataSource, argumentField: string): boolean {
    const argFields = this.getDatasourceArgumentFields(datasource)

    return argFields.filter((o) => o.dataField === argumentField)[0]?.dataType === 'date'
  }

  /** metrics show a single aggregated value with no argument grouping */
  getMetricSupportedDatasources(): ChartDataSource[] {
    return [
      ChartDataSource.Tasks,
      ChartDataSource.TaskDurations,
      ChartDataSource.Claims,
      ChartDataSource.Jobs,
      ChartDataSource.Cashflow,
      ChartDataSource.ForecastedTasks,
      ChartDataSource.ForecastedTasksInProgress,
      ChartDataSource.Extras,
      ChartDataSource.JobsBetweenTasks,
    ]
  }

  getArgFieldDisplayName(datasource: ChartDataSource, field: string): string {
    const fields = this.getDatasourceArgumentFields(datasource)

    return (
      fields.filter((o) => o.dataField === field)[0]?.displayName ??
      this.getUnavailableField(field).displayName
    )
  }

  /** stand-in for a saved field that is no longer offered (e.g. a disabled role no longer on any job) */
  getUnavailableField(dataField: string): ChartField {
    const role = this.getCurrentCompanyRoles().find((r) => r.key === dataField)
    return {
      dataField,
      displayName: `${role?.description ?? dataField} (unavailable)`,
      dataType: 'string',
      isPersonField: !!role,
      fieldGroup: role ? 'jobRole' : undefined,
    }
  }

  getValueFieldDisplayName(datasource: ChartDataSource, field: string): string {
    const fields = this.getDatasourceValueFields(datasource)

    return fields.filter((o) => o.dataField === field)[0]?.displayName
  }

  getDateFilterFieldDisplayName(datasource: ChartDataSource, field?: string): string {
    if (!field) {
      return ''
    }

    const filterFields = this.getDatasourceDateFilterFields(datasource)

    return filterFields.filter((o) => o.dataField === field)[0]?.displayName
  }

  getJobIdField(datasource: ChartDataSource): string {
    switch (datasource) {
      default:
        return JOBNUM_FIELD
    }
  }

  datasourceUsesTaskSelection(datasource: ChartDataSource): boolean {
    switch (datasource) {
      case ChartDataSource.Tasks:
      case ChartDataSource.TaskDurations:
      case ChartDataSource.ForecastedTasks:
      case ChartDataSource.ForecastedTasksInProgress:
      case ChartDataSource.JobsBetweenTasks:
        return true
      default:
        return false
    }
  }

  datasourceCustomGroupKeyFunc(datasource: ChartDataSource) {
    switch (datasource) {
      case ChartDataSource.ForecastedTasksInProgress:
        return FORECAST_TASK_IN_PROGRESS_GROUP_KEY_FUNC
      default:
        return undefined
    }
  }

  getTooltipValueString(datasource: ChartDataSource): string | undefined {
    switch (datasource) {
      case ChartDataSource.Cashflow:
        return 'Cash Flow'
      default:
        return undefined
    }
  }

  getDefaultValueAggregation(datasource: ChartDataSource): ValueAggregationType {
    switch (datasource) {
      case ChartDataSource.Cashflow:
        return ValueAggregationType.Cumulative
      default:
        return DEFAULT_VALUE_AGGREGATION
    }
  }

  getDefaultDateGrouping(datasource: ChartDataSource): DateAxisType {
    switch (datasource) {
      default:
        return DEFAULT_DATE_GROUPING
    }
  }

  getDefaultChartType(datasource: ChartDataSource): ChartType {
    switch (datasource) {
      case ChartDataSource.Cashflow:
        return ChartType.Line
      default:
        return DEFAULT_CHART_TYPE
    }
  }

  getDateRangeOptions(datasource: ChartDataSource) {
    switch (datasource) {
      case ChartDataSource.Cashflow:
      case ChartDataSource.ForecastedTasks:
      case ChartDataSource.ForecastedTasksInProgress:
        return FUTURE_DATE_RANGE_TYPES
      case ChartDataSource.Jobs:
      case ChartDataSource.JobsBetweenTasks:
        return Object.keys(DateRangeType).filter((k) => isNaN(Number(k))) // both past and future
      default:
        return PAST_DATE_RANGE_TYPES
    }
  }

  getDatasourceArgumentFields(datasource: ChartDataSource): ChartField[] {
    let fields: ChartField[] = []

    switch (datasource) {
      case ChartDataSource.Tasks:
      case ChartDataSource.TaskDurations:
        fields = [...JOBTASK_ARGUMENT_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Claims:
        fields = [...CLAIM_ARGUMENT_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Jobs:
      case ChartDataSource.JobsBetweenTasks:
        fields = [...JOB_ARGUMENT_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Cashflow:
        fields = [...CASHFLOW_ARGUMENT_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      // forecast arguments are limited to the forecast/target dates
      case ChartDataSource.ForecastedTasks:
        fields = FORECAST_TASKS_ARGUMENT_FIELDS
        break
      case ChartDataSource.ForecastedTasksInProgress:
        fields = FORECAST_TASKS_IN_PROGRESS_ARGUMENT_FIELDS
        break
      case ChartDataSource.Extras:
        fields = [...EXTRAS_ARGUMENT_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      default:
        this.logger.log(
          'datasource-config.service',
          'getDatasourceArgumentFields',
          null,
          'data source not implemented'
        )
        throw new Error('data source not implemented')
    }

    return this.sort(fields)
  }

  getDatasourceFilterFields(datasource: ChartDataSource): ChartField[] {
    let fields: ChartField[] = []

    switch (datasource) {
      case ChartDataSource.Tasks:
      case ChartDataSource.TaskDurations:
        fields = [...JOBTASK_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Claims:
        fields = [...CLAIM_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Jobs:
      case ChartDataSource.JobsBetweenTasks:
        fields = [...JOB_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Cashflow:
        fields = [...CASHFLOW_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.ForecastedTasks:
        fields = [...FORECAST_TASKS_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.ForecastedTasksInProgress:
        fields = [...FORECAST_TASKS_IN_PROGRESS_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Extras:
        fields = [...EXTRAS_FILTER_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      default:
        this.logger.log(
          'datasource-config.service',
          'getDatasourceFilterFields',
          null,
          'data source not implemented'
        )
        throw new Error('data source not implemented')
    }

    return this.sort(fields)
  }

  getDatasourceDateFilterFields(datasource: ChartDataSource): ChartField[] {
    let fields: ChartField[] = []

    switch (datasource) {
      case ChartDataSource.Tasks:
      case ChartDataSource.TaskDurations:
        fields = JOBTASK_DATE_FILTER_FIELDS
        break
      case ChartDataSource.Claims:
        fields = CLAIM_DATE_FILTER_FIELDS
        break
      case ChartDataSource.Jobs:
      case ChartDataSource.JobsBetweenTasks:
        fields = JOB_DATE_FILTER_FIELDS
        break
      case ChartDataSource.Cashflow:
        fields = CASHFLOW_DATE_FILTER_FIELDS
        break
      case ChartDataSource.ForecastedTasks:
        fields = FORECAST_TASKS_DATE_FILTER_FIELDS
        break
      case ChartDataSource.ForecastedTasksInProgress:
        fields = FORECAST_TASKS_IN_PROGRESS_DATE_FILTER_FIELDS
        break
      case ChartDataSource.Extras:
        fields = EXTRAS_DATE_FILTER_FIELDS
        break
      default:
        this.logger.log(
          'datasource-config.service',
          'getDatasourceDateFilterFields',
          null,
          'data source not implemented'
        )
        throw new Error('data source not implemented')
    }

    return this.sort(fields)
  }

  getDatasourceValueFields(datasource: ChartDataSource): ChartField[] {
    let fields: ChartField[] = []

    switch (datasource) {
      case ChartDataSource.Tasks:
        fields = JOBTASK_VALUE_FIELDS
        break
      case ChartDataSource.TaskDurations:
        fields = TASK_DURATION_VALUE_FIELDS
        break
      case ChartDataSource.Claims:
        fields = CLAIM_VALUE_FIELDS
        break
      case ChartDataSource.Jobs:
      case ChartDataSource.JobsBetweenTasks:
        fields = JOB_VALUE_FIELDS
        break
      case ChartDataSource.Cashflow:
        fields = CASHFLOW_VALUE_FIELDS
        break
      case ChartDataSource.ForecastedTasks:
        fields = FORECAST_TASKS_VALUE_FIELDS
        break
      case ChartDataSource.ForecastedTasksInProgress:
        fields = FORECAST_TASKS_IN_PROGRESS_VALUE_FIELDS
        break
      case ChartDataSource.Extras:
        fields = EXTRAS_VALUE_FIELDS
        break
      default:
        this.logger.log(
          'datasource-config.service',
          'getDatasourceValueFields',
          null,
          'data source not implemented'
        )
        throw new Error('data source not implemented')
    }

    return this.sort(fields)
  }

  getDatasourceSeriesGroupFields(datasource: ChartDataSource): ChartField[] {
    let fields: ChartField[] = []

    switch (datasource) {
      case ChartDataSource.Tasks:
        fields = [...JOBTASK_ARGUMENT_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.TaskDurations:
        fields = [...JOBTASK_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Claims:
        fields = [...CLAIM_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Jobs:
      case ChartDataSource.JobsBetweenTasks:
        fields = [...JOB_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Cashflow:
        fields = [...CASHFLOW_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.ForecastedTasks:
        fields = [...FORECAST_TASKS_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.ForecastedTasksInProgress:
        fields = [...FORECAST_TASKS_IN_PROGRESS_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      case ChartDataSource.Extras:
        fields = [...EXTRAS_SERIES_GROUP_FIELDS, ...this.getJobRolesForCurrentCompany()]
        break
      default:
        this.logger.log(
          'datasource-config.service',
          'getDatasourceSeriesGroupFields',
          null,
          'data source not implemented'
        )
        throw new Error('data source not implemented')
    }

    return this.sort(fields)
  }

  seriesGroupingIsDate(datasource: ChartDataSource, fieldName: string) {
    const fields = this.getDatasourceSeriesGroupFields(datasource)
    const field = fields.filter((f) => f.dataField === fieldName)[0]

    // a saved field that is no longer offered is treated as text so the chart still renders
    return field?.dataType === 'date'
  }

  /** Stores a company's roles from the job lookup - called by JobLookupApiService on each fetch */
  setJobRolesForCompany(companyId: number, roles: DashboardJobLookupRoleDto[]) {
    this.jobRolesByCompany.set(companyId, roles)
    this.jobRolesChangedSubject.next(companyId)
  }

  jobRolesLoadedForCurrentCompany(): boolean {
    const companyId = this.auth.getCurrentCompany()?.id
    return companyId != null && this.jobRolesByCompany.has(companyId)
  }

  private getCurrentCompanyRoles(): DashboardJobLookupRoleDto[] {
    const companyId = this.auth.getCurrentCompany()?.id
    if (companyId == null) return []
    return this.jobRolesByCompany.get(companyId) ?? []
  }

  private getJobRolesForCurrentCompany(): ChartField[] {
    return this.getCurrentCompanyRoles().filter(isRoleListed).map(buildRoleField)
  }

  /** Returns the dataField names of person-type fields currently used as argument or splitBy */
  getPersonFieldsInConfig(datasource: ChartDataSource, argumentField: string, splitByField: string): string[] {
    const allFields = [
      ...this.getDatasourceArgumentFields(datasource),
      ...this.getDatasourceSeriesGroupFields(datasource),
    ]
    const personFieldNames = new Set(allFields.filter(f => f.isPersonField).map(f => f.dataField))

    const result: string[] = []
    if (personFieldNames.has(argumentField)) result.push(argumentField)
    if (splitByField && personFieldNames.has(splitByField)) result.push(splitByField)
    return result
  }

  private sort(fields: ChartField[]): ChartField[] {
    return fields.sort((a, b) => (a.displayName < b.displayName ? -1 : 1))
  }
}
